import { createContext, useContext } from 'react';
import { UseEditFieldResult } from '@/features/dashboard/bookingChanges/components/editing/useEditField.ts';

/**
 * Stan dzielony między szkieletem ekranu a jego zakładkami.
 *
 * Szkielet (route nadrzędny) trzyma nagłówek, panel boczny, modale i filtr
 * „Tylko zmienione”; zakładki (route'y potomne) renderują samą treść. Wartości,
 * których obie strony potrzebują, idą przez kontekst Reacta, a nie przez
 * `context` route'u — to żywy stan Reacta (`useState`, wynik `useEditField`),
 * a kontekst route'u wylicza się w `beforeLoad` i nie odświeża się przy
 * przerysowaniu komponentu. Przez `search` też nie: filtr i akcje wiersza nie
 * są adresem.
 */
export interface DetailsContextValue {
  bookingId: string;
  /** Kod rezerwacji pokazywany w widokach rachunku; pusty, dopóki nie dojedzie. */
  bookingCode: string;
  isEditing: boolean;
  /** Filtr „Tylko zmienione” — przełącznik stoi w nagłówku, czyli w szkielecie. */
  onlyChanged: boolean;
  onOnlyChangedChange: (onlyChanged: boolean) => void;
  /** Wpięcie akcji wiersza w szkic paczki; modal edycji żyje w szkielecie. */
  editField: UseEditFieldResult;
  /**
   * Osoba, na której ekranie stoimy — uczestnik albo zapisujący. Ekran
   * rezerwacji jej nie ma, bo stoi na poziomie całej paczki.
   */
  currentPersonId?: string;
  /** Wejście na ekran rezerwacji („Raty i wpłaty →”, „Otwórz rezerwację →”). */
  onOpenBooking: () => void;
}

const DetailsContext = createContext<DetailsContextValue | null>(null);

export const DetailsProvider = DetailsContext.Provider;

/** Dostęp do stanu szkieletu z poziomu zakładki. */
export const useDetailsScreen = (): DetailsContextValue => {
  const value = useContext(DetailsContext);

  if (!value) {
    throw new Error(
      'useDetailsScreen działa wyłącznie wewnątrz szkieletu ekranu zmian rezerwacji.',
    );
  }

  return value;
};
